import * as logClient from "@classdojo/log-client";
import { MutationKey, useMutation, UseMutationResult } from "@tanstack/react-query";
import { UseMutationOptions } from "@tanstack/react-query";
import type { ApiResponseLike } from "@web-monorepo/infra/responseHandlers";
import {
  type ApiMethod,
  type ApiPath,
  APIRequestBody,
  APIRequestParameters,
  APIResponse,
  MethodQueryParameters,
} from "./types";
import { mergeMutationCallbacks, type MergedMutationContext } from "./mergeMutationCallbacks";
import { buildUrl } from "./urlBuilder";
import { isApiError, makeApiErrorMessage } from "./error";
import { urlPatternFromFetcher } from "./urlPatternFromFetcher";
import { sanitizeIds } from "./sanitizeIds";
import { handleSafariSpecialCase } from "./safariSpecialCase";
import { apiFetch, type Method as ApiFetchMethod } from "./apiFetch";

let _uid = 0;
const uniqueId = (prefix = "") => `${prefix}${++_uid}`;

export type OperationParams<Path extends ApiPath, Method extends ApiMethod<Path>> =
  APIRequestBody<Path, Method> extends never
    ? APIRequestParameters<Path, Method> & { headers?: Record<string, string> }
    : APIRequestParameters<Path, Method> & { body: APIRequestBody<Path, Method>; headers?: Record<string, string> };

export type OperationResult<Path extends ApiPath, Method extends ApiMethod<Path>> = Omit<ApiResponseLike, "body"> & {
  body: APIResponse<Path, Method>;
  text: string;
};

// React Query options exposed at the configuration level. Some options are
// facilitated by makeMutation and some would cause unexpected behavior with
// the internal logic.
type ExposedUseMutationOptions<TData, TError, TVariables, TContext = unknown> = Omit<
  UseMutationOptions<TData, TError, TVariables, TContext>,
  "mutationFn" | "mutationKey"
>;

type ErrorWithExpectedErrorField = Error & {
  isExpectedError?: boolean;
};

async function mutationLogic<TData, E extends Error, Params>(
  fn: (params: Params) => Promise<TData | E>,
  params: Params,
  mutationName: string,
): Promise<TData> {
  try {
    const result = await fn(params);

    // Currently, mutations support supplying their own fn that can
    // catch and return errors (to silence them). We should refactor
    // those mutations to use useErrorBoundary instead to optionally
    // throw errors encountered.
    if (result instanceof Error) {
      (result as ErrorWithExpectedErrorField).isExpectedError = true;
      throw result as Error;
    }

    void logClient.logEvent({
      eventName: `${logClient.getSite()}.mutation.${mutationName}.success`,
      automatedEvent: true,
    });

    // TS has some issues over void/undefined.
    return result;
  } catch (ex) {
    if (!(ex instanceof Error)) {
      throw ex;
    }

    if (isApiError(ex)) {
      ex.message = makeApiErrorMessage({
        path: sanitizeIds(ex.request.path),
        method: ex.request.method,
        response: ex.response,
        name: mutationName,
      });
    }

    void logClient.logEvent({
      eventName: `${logClient.getSite()}.mutation.${mutationName}.failure`,
      automatedEvent: true,
    });

    throw ex;
  }
}

export function makeMutation<Params, Result, E extends Error = Error, TContext = unknown>(
  config: {
    name: string;
    fn: (params: Params) => Promise<Result | E>;
  } & ExposedUseMutationOptions<Result, E, Params, TContext>,
): {
  // Each callback layer names its own snapshot shape: `TContext` for the factory's callbacks,
  // `THookContext` inferred from the hook caller's, so a call site can write an optimistic update
  // without the factory having to anticipate it. The *result*'s context stays `unknown` — what
  // react-query stores is the merged wrapper from `mergeMutationCallbacks`, not either snapshot.
  <THookContext = unknown>(
    options?: ExposedUseMutationOptions<Result, E, Params, THookContext>,
  ): UseMutationResult<Result, E, Params>;
  getMutationKey(): MutationKey;
} {
  const {
    name: mutationName,
    fn: mutationFn,
    onMutate: onMutateConfig,
    onSuccess: onSuccessConfig,
    onError: onErrorConfig,
    onSettled: onSettledConfig,
    ...extraConfig
  } = config;

  const mutationKey = [uniqueId(`${mutationName}_execution_`)];

  function useMutationWrapper<THookContext>(options: ExposedUseMutationOptions<Result, E, Params, THookContext> = {}) {
    const {
      onMutate: onMutateOption,
      onSuccess: onSuccessOption,
      onError: onErrorOption,
      onSettled: onSettledOption,
      ...extraOptions
    } = options;

    return useMutation<Result, E, Params, MergedMutationContext<TContext, THookContext>>(
      (params: Params) => mutationLogic<Result, E, Params>(mutationFn, params, mutationName),
      {
        mutationKey,
        useErrorBoundary: (error) => {
          // useErrorBoundary decides whether the error should be thrown
          // or not. If the error is not thrown, it will be returned as
          // mutation.error.
          return !("isExpectedError" in error || ("isExpected" in error && error.isExpected));
        },
        ...mergeMutationCallbacks<Result, E, Params, TContext, THookContext>(
          {
            onMutate: onMutateConfig,
            onSuccess: onSuccessConfig,
            onError: onErrorConfig,
            onSettled: onSettledConfig,
          },
          {
            onMutate: onMutateOption,
            onSuccess: onSuccessOption,
            onError: onErrorOption,
            onSettled: onSettledOption,
          },
        ),
        ...extraConfig,
        ...extraOptions,
      },
    );
  }

  useMutationWrapper.getMutationKey = () => mutationKey;

  return useMutationWrapper;
}

/**
 * Flatten a request's `path` and `query` bags into the single map `buildUrl` substitutes into the
 * pattern. Typed on `APIRequestParameters` rather than `OperationParams`: the latter's `& { body }`
 * intersection makes TS expand `Path` to every generated route and reject the bag shape, while the
 * bare parameters type resolves through the OpenAPI `parameters` constraint and needs no cast.
 */
function urlParamsOf<Path extends ApiPath, Method extends ApiMethod<Path>>(
  params: APIRequestParameters<Path, Method>,
): Record<string, unknown> {
  const bags: { path?: object; query?: object } = params;
  return { ...bags.path, ...bags.query };
}

export function makeApiMutation<
  Path extends ApiPath,
  Method extends ApiMethod<Path> & string,
  E extends Error = Error,
  QueryParams extends keyof MethodQueryParameters<Path, Method> = keyof MethodQueryParameters<Path, Method>,
  // Last, and it stays last: a new parameter ahead of `QueryParams` would silently rebind an
  // existing call site's 4th type argument and drop its query-key narrowing, with no type error.
  TContext = unknown,
>({
  name,
  path,
  queryParams,
  method,
  catchError,
  ...additionalOptions
}: {
  name: string;
  path: Path;
  queryParams?: QueryParams[];
  method: Method;
  catchError?: (err: E) => E;
} & ExposedUseMutationOptions<OperationResult<Path, Method>, E, OperationParams<Path, Method>, TContext>) {
  const route = path;
  const urlPattern = urlPatternFromFetcher(route, undefined, queryParams);

  return makeMutation<OperationParams<Path, Method>, OperationResult<Path, Method>, E, TContext>({
    name,
    fn: async (params: OperationParams<Path, Method>): Promise<E | OperationResult<Path, Method>> => {
      const pathToFetch = buildUrl({ urlPattern, params: urlParamsOf<Path, Method>(params) }, true);
      if (!pathToFetch) {
        throw new Error(`Unable to buildUrl for mutation: ${name}`);
      }

      const response = await apiFetch<OperationResult<Path, Method>["body"]>({
        method: method.toUpperCase() as ApiFetchMethod,
        path: pathToFetch,
        route,
        ...("body" in params && {
          body: params.body,
        }),
        ...("headers" in params && {
          headers: params.headers,
        }),
      });

      if (response.success) {
        return {
          body: response.data,
          text: response.text,
          status: response.status,
          // `statusCode` is the legacy alias apiFetch doesn't surface; mirror `status` so the
          // result satisfies `ApiResponseLike` for real rather than behind a cast.
          statusCode: response.status,
          headers: response.headers,
        };
      }

      const ex = response.error;
      if (isApiError(ex)) {
        handleSafariSpecialCase(ex, { path: route, method });
        ex.message = makeApiErrorMessage({
          path: route,
          method: method.toUpperCase(),
          response: ex.response,
          name,
        });
      }

      if (catchError) return catchError(ex as E);
      throw ex;
    },
    ...additionalOptions,
  });
}
